// src/scenes/restaurant/Reservations/Reservations.jsx
//
// Réservations de chambres. Rappels du modèle :
//   - une réservation créée est directement `confirmee` (pas d'étape de
//     confirmation côté serveur) ; les statuts possibles sont
//     en_attente / confirmee / annulee / terminee
//   - `check_in/` convertit la réservation en location et occupe la chambre :
//     la réservation passe alors en `terminee`
//   - `annuler/` refuse une réservation déjà annulée ou terminée
//   - la LISTE ne renvoie que les réservations actives (en_attente, confirmee) :
//     l'historique n'est pas exposé par cet endpoint.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Card, CardContent, CircularProgress,
  FormControl, InputLabel, Select, InputAdornment,
  Alert, Tooltip, Divider,
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  Add, Edit, Delete, Search, Login, EventBusy, Warning, Save, Event, Refresh,
} from '@mui/icons-material';
import { reservationAPI, chambreAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const STATUTS = {
  en_attente: { label: 'En attente', color: 'warning' },
  confirmee: { label: 'Confirmée', color: 'success' },
  annulee: { label: 'Annulée', color: 'error' },
  terminee: { label: 'Terminée', color: 'default' },
};

/** Demain 14 h — heure d'arrivée par défaut. */
const arriveeParDefaut = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(14, 0, 0, 0);
  return d;
};

/** Lendemain de l'arrivée à midi. */
const departParDefaut = (arrivee) => {
  const d = new Date(arrivee);
  d.setDate(d.getDate() + 1);
  d.setHours(12, 0, 0, 0);
  return d;
};

/** Date JS → valeur d'un <input type="datetime-local"> (heure locale). */
const versInputLocal = (date) => {
  const d = new Date(date);
  const decalage = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - decalage).toISOString().slice(0, 16);
};

const FORM_VIDE = () => {
  const arrivee = arriveeParDefaut();
  return {
    id: null,
    chambre: '',
    nom_client: '',
    telephone_client: '',
    email_client: '',
    date_debut: versInputLocal(arrivee),
    date_fin: versInputLocal(departParDefaut(arrivee)),
    montant_acompte: '',
    notes: '',
  };
};

const fmt = (n) => new Intl.NumberFormat('fr-HT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(Number(n) || 0) + ' HTG';

const fmtDateHeure = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR', {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

/** Nombre de nuits entre deux dates, au moins 1. */
const nuits = (debut, fin) => {
  const d = new Date(debut);
  const f = new Date(fin);
  if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime())) return 0;
  return Math.max(0, Math.round((f - d) / (24 * 3600 * 1000)));
};

const versTableau = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const messageErreur = (error, defaut) => {
  const d = error.response?.data;
  if (!d) return error.message || defaut;
  if (typeof d === 'string') return d;
  if (d.error) return d.error;
  if (d.detail) return d.detail;
  // DRF met les erreurs de `validate()` sous non_field_errors
  if (Array.isArray(d.non_field_errors)) return d.non_field_errors[0];
  const premier = Object.entries(d)[0];
  if (premier) {
    const [champ, valeur] = premier;
    return `${champ} : ${Array.isArray(valeur) ? valeur[0] : valeur}`;
  }
  return defaut;
};

const ReservationsPage = () => {
  const [reservations, setReservations] = useState([]);
  const [chambres, setChambres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(FORM_VIDE);

  const fetchTout = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rRes, rCh] = await Promise.all([
        reservationAPI.getAll(),
        chambreAPI.getAll(),
      ]);
      setReservations(versTableau(rRes.data));
      setChambres(versTableau(rCh.data));
    } catch (err) {
      const msg = messageErreur(err, 'Erreur lors du chargement des réservations');
      setError(msg);
      toast.error(msg);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTout(); }, [fetchTout]);

  const chambresById = useMemo(
    () => new Map(chambres.map((c) => [c.id, c])),
    [chambres],
  );

  const handleOpenDialog = (reservation = null) => {
    if (reservation) {
      setFormData({
        id: reservation.id,
        chambre: reservation.chambre || '',
        nom_client: reservation.nom_client || '',
        telephone_client: reservation.telephone_client || '',
        email_client: reservation.email_client || '',
        date_debut: reservation.date_debut ? versInputLocal(reservation.date_debut) : '',
        date_fin: reservation.date_fin ? versInputLocal(reservation.date_fin) : '',
        montant_acompte: reservation.montant_acompte ?? '',
        notes: reservation.notes || '',
      });
      setIsEditing(true);
    } else {
      setFormData(FORM_VIDE());
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.chambre) return toast.error('Choisissez une chambre');
    if (!formData.nom_client.trim()) return toast.error('Le nom du client est requis');
    if (!formData.date_debut || !formData.date_fin) return toast.error('Les dates sont requises');
    if (new Date(formData.date_fin) <= new Date(formData.date_debut)) {
      return toast.error("La date de départ doit être après la date d'arrivée");
    }

    setSubmitting(true);
    try {
      const corps = {
        chambre: formData.chambre,
        nom_client: formData.nom_client.trim(),
        telephone_client: formData.telephone_client.trim() || undefined,
        email_client: formData.email_client.trim() || undefined,
        date_debut: new Date(formData.date_debut).toISOString(),
        date_fin: new Date(formData.date_fin).toISOString(),
        montant_acompte: Number(formData.montant_acompte) || 0,
        notes: formData.notes.trim() || undefined,
      };

      if (isEditing) {
        await reservationAPI.update(formData.id, corps);
        toast.success('Réservation modifiée');
      } else {
        await reservationAPI.create(corps);
        toast.success('Réservation enregistrée');
      }
      setOpenDialog(false);
      fetchTout();
    } catch (err) {
      // Le serveur refuse une chambre déjà réservée sur la période
      toast.error(messageErreur(err, "Erreur lors de l'enregistrement"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckIn = async (reservation) => {
    const chambre = chambresById.get(reservation.chambre);
    if (!window.confirm(
      `Faire entrer ${reservation.nom_client} en chambre ${reservation.chambre_numero || chambre?.numero} ?`,
    )) return;
    try {
      await reservationAPI.checkIn(reservation.id);
      toast.success('Check-in effectué — la chambre est maintenant occupée');
      fetchTout();
    } catch (err) {
      toast.error(messageErreur(err, 'Check-in impossible'));
    }
  };

  const handleAnnuler = async (reservation) => {
    if (!window.confirm(`Annuler la réservation de ${reservation.nom_client} ?`)) return;
    try {
      await reservationAPI.cancel(reservation.id);
      toast.success('Réservation annulée');
      fetchTout();
    } catch (err) {
      toast.error(messageErreur(err, "Erreur lors de l'annulation"));
    }
  };

  const handleDelete = async (reservation) => {
    if (!window.confirm(
      `Supprimer définitivement la réservation de ${reservation.nom_client} ?\n`
      + 'Pour garder une trace, préférez « Annuler ».',
    )) return;
    try {
      await reservationAPI.delete(reservation.id);
      toast.success('Réservation supprimée');
      fetchTout();
    } catch (err) {
      toast.error(messageErreur(err, 'Erreur lors de la suppression'));
    }
  };

  const filtrees = useMemo(() => {
    const recherche = searchTerm.toLowerCase();
    return reservations.filter((r) => {
      const numero = r.chambre_numero || chambresById.get(r.chambre)?.numero || '';
      const correspondRecherche =
        r.nom_client?.toLowerCase().includes(recherche) ||
        r.telephone_client?.toLowerCase().includes(recherche) ||
        String(numero).toLowerCase().includes(recherche) ||
        false;
      const correspondStatut = filtreStatut === 'all' || r.statut === filtreStatut;
      return correspondRecherche && correspondStatut;
    });
  }, [reservations, searchTerm, filtreStatut, chambresById]);

  const stats = useMemo(() => {
    const aujourdhui = new Date().toDateString();
    return {
      total: reservations.length,
      confirmees: reservations.filter((r) => r.statut === 'confirmee').length,
      arriveesDuJour: reservations.filter(
        (r) => r.date_debut && new Date(r.date_debut).toDateString() === aujourdhui,
      ).length,
      acomptes: reservations.reduce((s, r) => s + (Number(r.montant_acompte) || 0), 0),
    };
  }, [reservations]);

  const chambreChoisie = chambresById.get(Number(formData.chambre));
  const nbNuits = nuits(formData.date_debut, formData.date_fin);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          icon={<Warning />}
          sx={{ borderRadius: 2 }}
          action={<Button variant="outlined" size="small" onClick={fetchTout}>Réessayer</Button>}
        >
          <Typography variant="h6" gutterBottom>Erreur de chargement</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <Event sx={{ mr: 2, verticalAlign: 'middle' }} />
              Réservations
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Arrivées à venir et acomptes encaissés
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <TextField
              placeholder="Client, téléphone, chambre..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ width: 280 }}
            />
            <Tooltip title="Rafraîchir">
              <IconButton onClick={fetchTout}><Refresh /></IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()} sx={{ height: 40, px: 3 }}>
              Nouvelle réservation
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Réservations actives</Typography>
                <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Confirmées</Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">{stats.confirmees}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Arrivées aujourd'hui</Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.arriveesDuJour}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 2, height: '100%' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">Acomptes encaissés</Typography>
                <Typography variant="h4" fontWeight="bold">{fmt(stats.acomptes)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Box mb={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Statut</InputLabel>
          <Select value={filtreStatut} label="Statut" onChange={(e) => setFiltreStatut(e.target.value)}>
            <MenuItem value="all">Tous les statuts</MenuItem>
            {Object.entries(STATUTS).map(([cle, s]) => (
              <MenuItem key={cle} value={cle}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          Les réservations annulées et terminées ne sont plus listées.
        </Typography>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Chambre</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Arrivée</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Départ</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Nuits</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Acompte</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      {searchTerm || filtreStatut !== 'all'
                        ? 'Aucune réservation ne correspond aux critères'
                        : 'Aucune réservation en cours'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtrees.map((reservation) => {
                  const statut = STATUTS[reservation.statut] || { label: reservation.statut, color: 'default' };
                  const chambre = chambresById.get(reservation.chambre);
                  const nb = nuits(reservation.date_debut, reservation.date_fin);
                  return (
                    <TableRow key={reservation.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium">{reservation.nom_client || '—'}</Typography>
                        {reservation.telephone_client && (
                          <Typography variant="caption" color="textSecondary">
                            {reservation.telephone_client}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {reservation.chambre_numero || chambre?.numero || '—'}
                        </Typography>
                        {reservation.chambre_type && (
                          <Typography variant="caption" color="textSecondary">
                            {reservation.chambre_type}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{fmtDateHeure(reservation.date_debut)}</TableCell>
                      <TableCell>{fmtDateHeure(reservation.date_fin)}</TableCell>
                      <TableCell align="center">{nb || '—'}</TableCell>
                      <TableCell align="right">
                        {Number(reservation.montant_acompte) > 0
                          ? <Typography color="success.main" fontWeight="medium">{fmt(reservation.montant_acompte)}</Typography>
                          : <Typography variant="body2" color="textSecondary">—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip label={statut.label} size="small" color={statut.color} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          {reservation.statut === 'confirmee' && (
                            <Tooltip title="Check-in — convertit en location et occupe la chambre">
                              <IconButton color="success" size="small" onClick={() => handleCheckIn(reservation)}>
                                <Login />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Modifier">
                            <IconButton color="primary" size="small" onClick={() => handleOpenDialog(reservation)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Annuler (garde la trace)">
                            <IconButton color="warning" size="small" onClick={() => handleAnnuler(reservation)}>
                              <EventBusy />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer définitivement">
                            <IconButton color="error" size="small" onClick={() => handleDelete(reservation)}>
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Création / modification */}
      <Dialog
        open={openDialog}
        onClose={() => !submitting && setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Event />
            <Typography variant="h6">
              {isEditing ? 'Modifier la réservation' : 'Nouvelle réservation'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2.5}>
            {/* ── Chambre et séjour ───────────────────────────────────── */}
            <Grid item xs={12}>
              <Typography variant="overline" color="text.secondary">Chambre et séjour</Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth required size="small">
                <InputLabel>Chambre</InputLabel>
                <Select
                  value={formData.chambre}
                  label="Chambre"
                  onChange={(e) => setFormData({ ...formData, chambre: e.target.value })}
                  disabled={submitting}
                >
                  {chambres.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      Chambre {c.numero} — {c.type_chambre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Arrivée"
                type="datetime-local"
                fullWidth
                size="small"
                value={formData.date_debut}
                onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Départ"
                type="datetime-local"
                fullWidth
                size="small"
                value={formData.date_fin}
                onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                InputLabelProps={{ shrink: true }}
                error={!!formData.date_fin && new Date(formData.date_fin) <= new Date(formData.date_debut)}
                helperText={
                  formData.date_fin && new Date(formData.date_fin) <= new Date(formData.date_debut)
                    ? "Doit être après l'arrivée"
                    : ' '
                }
                disabled={submitting}
              />
            </Grid>

            {/* Estimation du séjour, calculée en direct */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Séjour : <strong>{nbNuits || '—'} nuit{nbNuits > 1 ? 's' : ''}</strong>
                </Typography>
                {chambreChoisie && nbNuits > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Estimation :{' '}
                    <strong>
                      {fmt(
                        (Number(
                          chambreChoisie.location_sejour_active && nbNuits > 1
                            ? chambreChoisie.prix_sejour
                            : chambreChoisie.prix_nuit,
                        ) || 0) * nbNuits,
                      )}
                    </strong>
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  Le prix définitif est fixé au check-in.
                </Typography>
              </Paper>
            </Grid>

            {/* ── Client ──────────────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Client</Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Nom du client"
                fullWidth
                size="small"
                value={formData.nom_client}
                onChange={(e) => setFormData({ ...formData, nom_client: e.target.value })}
                required
                error={!formData.nom_client.trim()}
                helperText={!formData.nom_client.trim() ? 'Ce champ est requis' : ' '}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Téléphone"
                fullWidth
                size="small"
                value={formData.telephone_client}
                onChange={(e) => setFormData({ ...formData, telephone_client: e.target.value })}
                placeholder="0X XX XX XX"
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="E-mail"
                type="email"
                fullWidth
                size="small"
                value={formData.email_client}
                onChange={(e) => setFormData({ ...formData, email_client: e.target.value })}
                disabled={submitting}
              />
            </Grid>

            {/* ── Acompte et notes ────────────────────────────────────── */}
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="overline" color="text.secondary">Acompte et notes</Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Acompte"
                type="number"
                fullWidth
                size="small"
                value={formData.montant_acompte}
                onChange={(e) => setFormData({ ...formData, montant_acompte: e.target.value })}
                InputProps={{
                  endAdornment: <InputAdornment position="end">HTG</InputAdornment>,
                  inputProps: { step: '0.01', min: '0' },
                }}
                helperText="Optionnel"
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                label="Notes"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Arrivée tardive, lit bébé..."
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Une réservation enregistrée est directement confirmée. Le serveur refuse
                une chambre déjà réservée sur la période.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={() => !submitting && setOpenDialog(false)} variant="outlined" disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.chambre || !formData.nom_client.trim()}
            startIcon={submitting ? <CircularProgress size={20} /> : (isEditing ? <Save /> : <Add />)}
          >
            {submitting ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReservationsPage;
